import { Redirect } from 'react-router-dom';
import { useTheme, Subheader } from '@streem/ui-react';
import { APITypes } from '@streem/api';
import { observer } from 'mobx-react';
import { columnBuilder, CallLogColumnProps } from '../../components/calls/call_log_columns';
import { FlexCentered } from '../../forms/shared_form.styles';
import { useGlobalStore } from '../../hooks/use_global_context';
import { useGetUserCallLogStore } from '../../hooks/list_store_hooks';
import { ListViewWithPagination } from '../../components/lists/list_view_with_pagination';
import { useUpdateMyCallLogFilters } from '../../hooks/use_update_my_calls_filters';
import { useCallback, useMemo } from 'react';
import { useCallLogPage } from '../../hooks/use_call_log_page';

function buildMyCallLogColumns(props: CallLogColumnProps) {
    const cols = [
        columnBuilder.customerColumn(props),
        columnBuilder.artifactColumn(props),
        columnBuilder.durationColumn(),
        columnBuilder.dateColumn(),
    ];

    return cols;
}

interface MyCallLogPageProps {
    searchString: string;
}

interface CallLogRow extends APITypes.StreemApiCallLogEntry {
    key: string;
}

export const MyCallLogPage = observer(({ searchString }: MyCallLogPageProps) => {
    const theme = useTheme();
    const { companySettingsStore, uiStore } = useGlobalStore();
    const userCallLogStore = useGetUserCallLogStore();
    const callOutcomeEnabled = !!companySettingsStore.roomOutcomeConfig;
    const sendNoteEnabled = companySettingsStore.sendNoteEnabled;
    const { isRowClickable, rowClickUrl } = useCallLogPage();

    useUpdateMyCallLogFilters(userCallLogStore, {
        requestSourceCodes: uiStore.appliedSourceFiltersSelected,
        callTransferredFilterSelected: uiStore.callTransferFilterSelected,
        searchStringFilter: searchString,
    });

    const callLogRecords: CallLogRow[] = userCallLogStore.results.map(v => ({
        key: v.roomSid!,
        ...v,
    }));

    const hideGPSLocations = !companySettingsStore.gpsEnabled;

    const columns = useMemo(
        () => [
            ...buildMyCallLogColumns({
                theme,
                hideGPSLocations,
                ...{ callOutcomeEnabled, sendNoteEnabled },
            }),
        ],
        [callOutcomeEnabled, hideGPSLocations, sendNoteEnabled, theme],
    );

    const buildRowTestId = useCallback((row: CallLogRow) => 'my-call-log-row-' + row.roomSid, []);

    if (userCallLogStore.lastError && userCallLogStore.lastError.status === 500) {
        return <Redirect to="/error" />;
    }

    if (!userCallLogStore.loading && callLogRecords.length === 0 && searchString.length) {
        return (
            <FlexCentered>
                <Subheader>No Streem calls were found for {searchString}.</Subheader>
            </FlexCentered>
        );
    }

    if (!userCallLogStore.loading && callLogRecords.length === 0) {
        return (
            <FlexCentered>
                <Subheader>You have not made any calls yet.</Subheader>
            </FlexCentered>
        );
    }

    return (
        <ListViewWithPagination
            initialScrollPosition={userCallLogStore.scrollPosition}
            store={userCallLogStore}
            data-testid="my-call-log"
            loading={userCallLogStore.loading}
            linkTo={rowClickUrl}
            canRowClick={isRowClickable}
            data={callLogRecords}
            buildRowTestId={buildRowTestId}
            columns={columns}
            gridTemplateColumns={`minmax(300px, 1fr) 250px 170px 170px`}
        />
    );
});
